import { formatIDR } from '@/lib/format'

import type {
  AnalyticsResponse,
  PerformanceDataset,
  PerformanceMetric,
  PerformanceMetricId,
  PerformancePoint,
  ReportAggregation,
  ReportMetricKey,
} from './schema'

/**
 * Adapts the raw `GET /v1/algo-pos-be/liotitip/analytics` payload (WEBLP-3265)
 * into the `PerformanceDataset` the chart UI consumes. Keeping this mapping in
 * one place means the components never see the snake_case contract, and a
 * contract change only touches `schema.ts` + this file.
 */

interface MetricConfig {
  id: PerformanceMetricId
  /** Shared field name across `totals`, each bucket, and `change_percent`. */
  key: ReportMetricKey
  label: string
  currency: boolean
  chartTitle: Record<ReportAggregation, string>
}

/** Maps each UI metric to its backend field and display copy. Drives card order. */
const METRICS: MetricConfig[] = [
  {
    id: 'revenue',
    key: 'total_sale',
    label: 'Penjualan',
    currency: true,
    chartTitle: {
      day: 'Penjualan per hari',
      week: 'Penjualan per minggu',
      month: 'Penjualan per bulan',
    },
  },
  {
    id: 'orders',
    key: 'total_orders',
    label: 'Pesanan',
    currency: false,
    chartTitle: {
      day: 'Pesanan per hari',
      week: 'Pesanan per minggu',
      month: 'Pesanan per bulan',
    },
  },
  {
    id: 'aov',
    key: 'sale_per_order',
    label: 'Penjualan per Pesanan',
    currency: true,
    chartTitle: {
      day: 'Rata-rata nilai pesanan per hari',
      week: 'Rata-rata nilai pesanan per minggu',
      month: 'Rata-rata nilai pesanan per bulan',
    },
  },
  {
    id: 'buyers',
    key: 'total_customer',
    label: 'Pembeli',
    currency: false,
    chartTitle: {
      day: 'Pembeli unik per hari',
      week: 'Pembeli unik per minggu',
      month: 'Pembeli unik per bulan',
    },
  },
]

const shortMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mei',
  'Jun',
  'Jul',
  'Agu',
  'Sep',
  'Okt',
  'Nov',
  'Des',
]
const shortWeekdays = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab']

/** `9.7` → `+9,7%`; `-3.2` → `-3,2%`; `0` → `0%`. Indonesian decimal comma. */
function formatChange(change: number): string {
  const sign = change > 0 ? '+' : change < 0 ? '-' : ''
  const text = Math.abs(change).toFixed(1).replace('.', ',')
  return `${sign}${text}%`
}

function formatMetricValue(value: number, currency: boolean): string {
  return currency ? formatIDR(value) : value.toLocaleString('id-ID')
}

/**
 * Turns a bucket's `date` into the chart's x-axis label.
 *
 * `date` carries whatever precision the backend's date format emits for the
 * bucket: if it ever includes a time component (e.g. `2026-10-23 12:00` or
 * `2026-10-23T12:00:00`), it reads as a clock label (`12:00`). A bare
 * `YYYY-MM-DD` reads as a weekday + day (`Jum 23`) for day buckets, the week's
 * first day (`23 Okt`) for week buckets, or a short month (`Okt`) for month
 * buckets. Anything else passes through as-is.
 */
function formatPointLabel(rawDate: string, aggregation: ReportAggregation): string {
  const timeMatch = /(\d{1,2}):(\d{2})/.exec(rawDate)
  if (timeMatch) return `${timeMatch[1]!.padStart(2, '0')}:${timeMatch[2]}`

  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(rawDate)
  if (!dateMatch) return rawDate

  const [, year, month, day] = dateMatch
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  if (Number.isNaN(date.getTime())) return rawDate

  if (aggregation === 'month') return shortMonths[date.getMonth()]!
  if (aggregation === 'week') return `${date.getDate()} ${shortMonths[date.getMonth()]}`
  return `${shortWeekdays[date.getDay()]} ${date.getDate()}`
}

export function toDataset(response: AnalyticsResponse): PerformanceDataset {
  const { aggregation, current, change_percent: changePercent } = response

  const metrics: PerformanceMetric[] = METRICS.map((config) => {
    const value = current.totals[config.key]
    const change = changePercent[config.key]
    const series: PerformancePoint[] = current.buckets.map((bucket) => ({
      label: formatPointLabel(bucket.date, aggregation),
      value: bucket[config.key],
    }))

    return {
      id: config.id,
      label: config.label,
      display: formatMetricValue(value, config.currency),
      // `null` means the previous period had no baseline to compare against.
      delta: change === null ? '–' : formatChange(change),
      deltaPositive: change === null ? null : change >= 0,
      chartTitle: config.chartTitle[aggregation],
      currency: config.currency,
      series,
    }
  })

  return { metrics }
}
